<script setup lang="ts">
import type { ContentAuthor } from '~/models/Content/ContentAuthor'
import type { ContentArticle } from '~/models/Content/ContentArticle'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { createReusableTemplate } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { faChevronDown } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import useArrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { useMedia } from '~/composables/useMedia'
import useFullscreen from '~/composables/useFullscreen'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Image from '~/components/media/Image.vue'
import SubArticleNavigation from '~/components/article/SubArticleNavigation.vue'
import ArticleHeaderContent from '~/components/article/ArticleHeaderContent.vue'
import ArticleHeaderBar from '~/components/article/ArticleHeaderBar.vue'

const { article, subArticles, routeParams, hasSequenceSection } = defineProps<{
  article: ContentArticle
  routeParams: Record<string | number, string | number>
  subArticles?: Array<ContentArticle>
  hasSequenceSection?: boolean
}>()
const { isFullscreen } = useFullscreen()
const { isTeacher } = storeToRefs(useAuthStore())

const { findContents } = useContentApi()
const { truthy } = useArrayUtils()

const [DefineSubArticleMenu, ReuseSubArticleMenu] = createReusableTemplate()

const imageContentIds = computed(() => [Number(article.image?.destinationContentId ?? 0)].filter(truthy<number>))
const { data: image } = firstOf(useMedia(imageContentIds))
const leftColumnHeader = computed(() => article.headerVariation?.identifier === 'left_column')

const { data: authors } = useQuery({
  staleTime: Infinity,
  queryKey: ['app-article-authors', article.locationId],
  queryFn: () => findContents<ContentAuthor>({
    contentIdCriterion: article.authors!.destinationContentIds,
    contentTypeCriterion: [ContentType.Author],
    subtreeCriterion: [Subtree.Authors],
    mainLocationCriterion: true,
  }),
  enabled: computed(() => !!article.authors?.destinationContentIds.length)
})
</script>

<template>
  <DefineSubArticleMenu>
    <SubArticleNavigation
      v-if="subArticles && !leftColumnHeader"
      class="z-1 col-start-1 row-start-1 mt-[20px] self-start md:mt-[22px]"
      :class="isFullscreen || !isTeacher ? 'ml-5' : 'ml-20'"
      :article
      :sub-articles
      :route-params
    />
  </DefineSubArticleMenu>

  <header
    v-if="article.headerVariation.identifier === 'text_on_image'"
    class="col-[fill] grid grid-flow-row grid-cols-subgrid grid-rows-[50vh] gap-y-6"
  >
    <ReuseSubArticleMenu />
    <div class="col-start-1 -col-end-1 row-start-1 row-end-1">
      <Image
        v-if="image"
        class="!size-full min-h-full object-cover"
        :content="image"
      />
    </div>
    <CopyrightButton
      v-if="image && 'copyright' in image"
      class="-col-end-2 row-start-1 row-end-2 mt-auto ml-auto"
      :copyright="image.copyright"
      color-theme
    />
    <div class="col-[extext] grid grid-cols-subgrid self-end bg-(--theme-5) md:row-start-1 md:rounded-t-lg md:pt-(--col-width)">
      <div
        v-if="article.overline"
        class="col-[text] font-semibold uppercase"
        v-text="article.overline"
      />
      <h1 class="au-title col-[text] font-bold" v-text="article.title" />
    </div>
    <div class="col-[text] flex flex-col gap-6">
      <div v-if="article.intro" class="au-subtitle au-prose contents">
        <RichTextRenderer :text="article.intro" />
      </div>
      <div
        v-if="authors"
        class="mt-auto"
      >
        {{ $t('article.text') }}:
        <strong
          v-for="(author, index) in authors"
          :key="author.contentId"
        >
          {{ author.title }}<template v-if="index + 1 < authors.length">, </template>
        </strong>
      </div>
      <ArticleHeaderBar :article="article" />
      <font-awesome-icon
        v-if="hasSequenceSection"
        :icon="faChevronDown"
        class="mx-auto mt-8"
        beat-fade
        style="--fa-animation-duration: 2s"
      />
    </div>
  </header>

  <template v-else-if="article.headerVariation.identifier === 'text_and_image'">
    <ReuseSubArticleMenu />
    <header class="col-[media] pt-24">
      <div class="grid grid-cols-1 text-(--theme-pop) md:grid-cols-2">
        <ArticleHeaderContent :article="article" class="bg-(--theme-60) p-(--col-width) max-md:rounded-t-lg md:rounded-l-lg" />
        <div class="overflow-hidden bg-(--theme-10) p-(--col-gap) max-md:order-1 max-md:rounded-b-lg md:rounded-r-lg">
          <FullscreenContent class="relative aspect-[10/9] size-full" bg-class="bg-(--theme-10)">
            <template #default="{ isFullscreen }">
              <Image
                v-if="image"
                class="col-start-1 row-start-1"
                :class="isFullscreen ? 'p-4 place-self-center object-scale-down' : 'max-h-full min-h-full object-cover'"
                :content="image"
                :use-focal-point="!isFullscreen"
              />
              <CopyrightButton
                v-if="image && 'copyright' in image"
                class="col-start-1 row-start-1 place-self-end"
                :copyright="image.copyright"
                color-theme
              />
            </template>
          </FullscreenContent>
        </div>
      </div>
      <ArticleHeaderBar :article="article" />
      <font-awesome-icon
        v-if="hasSequenceSection"
        :icon="faChevronDown"
        class="mx-auto mt-8"
        beat-fade
        style="--fa-animation-duration: 2s"
      />
    </header>
  </template>

  <header
    v-else-if="article.headerVariation.identifier === 'color'"
    class="col-start-1 -col-end-1 grid grid-cols-subgrid bg-(--theme-60) text-(--theme-pop)"
  >
    <ReuseSubArticleMenu />
    <div class="col-[text] flex flex-col gap-6 pt-32 pb-(--col-width)">
      <ArticleHeaderContent :article="article" />
      <ArticleHeaderBar :article="article" />
      <font-awesome-icon
        v-if="hasSequenceSection"
        :icon="faChevronDown"
        class="mx-auto mt-8"
        beat-fade
        style="--fa-animation-duration: 2s"
      />
    </div>
  </header>

  <header
    v-else-if="article.headerVariation.identifier === 'simple'"
    class="col-start-1 -col-end-1 grid grid-cols-subgrid"
  >
    <ReuseSubArticleMenu />
    <div class="col-[text] flex flex-col gap-6 pt-32">
      <ArticleHeaderContent :article="article" />
      <ArticleHeaderBar :article="article" />
      <font-awesome-icon
        v-if="hasSequenceSection"
        :icon="faChevronDown"
        class="mx-auto mt-8"
        beat-fade
        style="--fa-animation-duration: 2s"
      />
    </div>
  </header>

  <header
    v-else
    class="col-start-1 -col-end-1"
  >
    <ReuseSubArticleMenu />
    <h1 class="sr-only" v-text="article.title" />
  </header>
</template>
